import { withLifeErrorBoundary } from '@festina-life/components'
import { dateUtils } from '@festina-life/core'
import { Alert, FullscreenSpinner } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { withSuspense } from '@festina-life/ui/libs'
import { Group, Panel, useDefaultLayout } from 'react-resizable-panels'

import { FPlusDiffWorkerPoolProvider } from '../../../components/fplusDiff/FPlusDiffWorkerPoolProvider'
import { useConfigurationContext } from '../../../context/configurationContext'
import { useTemplateId } from '../../../hooks/useTemplateId'
import { useNavigationParams } from '../../../system/useNavigationParams'
import {
  useTemplateVersionInfo,
  useTemplateVersionInfos,
} from '../../../uiModels/versionInfo/useTemplateVersionInfo'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { TemplateDetailsModal } from '../../layout/TemplateDetailsModal'
import { TemplateDetailsRightSidebarPanel } from '../../layout/TemplateDetailsRightSidebarPanel'
import { TemplateSidebarPanel } from '../TemplateSidebarPanel'
import { TemplateCentralView } from './TemplateCentralView'

/**
 * Viewer for visualizing a template. This view is configured based on the current configuration of
 * the sidebar and top-bar controls.
 */
const TemplateViewerInternal = () => {
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    groupId: 'templateViewerPanel',
    storage: localStorage,
  })
  const translate = useTranslate()
  const { viewAt } = useConfigurationContext()
  const { isLoading } = useOperationsContext()
  const templateId = useTemplateId()
  const versionInfos = useTemplateVersionInfos(templateId)
  const versionInfo = useTemplateVersionInfo({
    id: templateId,
    date: viewAt,
  })
  const { block } = useNavigationParams()

  if (isLoading) {
    return <FullscreenSpinner position="relative" />
  }

  if (!versionInfo) {
    return (
      <div className="flex size-full items-center justify-center">
        {versionInfos.length ? (
          <Alert
            severity="warning"
            title={translate('warning.noDataForVersion', {
              viewAt: dateUtils.dateAsStr(viewAt),
              nextViewAtDate: versionInfos.length
                ? dateUtils.dateAsStr(versionInfos[0].fromDate)
                : '',
            })}
          />
        ) : (
          <Alert
            severity="error"
            title={translate('warning.noDataAvailable', {
              itemId: templateId,
              block,
            })}
          />
        )}
      </div>
    )
  }

  return (
    <>
      <Group
        orientation="horizontal"
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
        className="size-full"
      >
        <TemplateSidebarPanel />
        <Panel className="size-full overflow-hidden" id="templateCentralView">
          <TemplateCentralView key={templateId} versionInfo={versionInfo} />
        </Panel>
        <TemplateDetailsRightSidebarPanel />
      </Group>
      <TemplateDetailsModal versionInfo={versionInfo} />
    </>
  )
}

/**
 * Every F-plus comparison lives inside the template scene, so the pool that
 * highlights diffs off the main thread is started here rather than at startup.
 * It is deliberately above the diff tooltips: those mount and unmount on hover,
 * and the pool is torn down with its caches whenever its last consumer leaves.
 */
const TemplateViewerWithFPlusDiffs = () => (
  <FPlusDiffWorkerPoolProvider>
    <TemplateViewerInternal />
  </FPlusDiffWorkerPoolProvider>
)

export const TemplateViewer = withSuspense(
  withLifeErrorBoundary(TemplateViewerWithFPlusDiffs),
)
